import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RiskService } from '../../../services/riskService';
import { SharedService } from '../../../services/sharedService';
import { RiskBreakdownComponent } from '../risk-breakdown/risk-breakdown.component';
import { saveAs } from 'file-saver';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';

@Component({
  selector: 'risk-results',
  templateUrl: './risk-results.component.html',
  styleUrls: ['./risk-results.component.css'],
})
export class RiskResultsComponent implements OnInit {
  years: any[] = [];
  sectors: Array<{ name: string; label: string }>;
  selectedSector: { [year: string]: string } = {};

  indicators: { [year: string]: Array<{ id: string; name: string }> } = {};
  records: { [year: string]: any[] } = {};
  total: { [year: string]: number } = {};
  page: { [year: string]: number } = {};
  pageSize = 20;
  loading: { [year: string]: boolean } = {};
  exporting: { [year: string]: boolean } = {};
  search: { [year: string]: string } = {};
  colorFilter: { [year: string]: string } = {};
  private searchTimers: { [year: string]: any } = {};

  activeTabId = 0;
  @Input() disableHeader = false;

  isMobile$: any;
  mobileFiltersOpen = false;

  get riskActiveFilterCount(): number {
    const year = this.years[this.activeTabId];
    if (!year) { return 0; }
    let count = 0;
    if (this.search[year]) { count++; }
    if (this.colorFilter[year]) { count++; }
    if (this.selectedSector[year] && this.selectedSector[year] !== 'Default') { count++; }
    return count;
  }

  constructor(
    private riskService: RiskService,
    private sharedService: SharedService,
    private router: Router,
    private modalService: NgbModal,
    private breakpointObserver: BreakpointObserver
  ) {
    this.isMobile$ = this.breakpointObserver
      .observe(Breakpoints.Handset)
      .pipe(map(result => result.matches));
  }

  ngOnInit() {
    this.sharedService.loadMenuItems('indicator');
    this.sectors = this.riskService.getSectors();
    this.riskService.getYears().subscribe(years => {
      this.years = years.slice().sort((a, b) => Number(b) - Number(a));
      years.forEach(year => {
        this.selectedSector[year] = 'Default';
        this.search[year] = '';
        this.colorFilter[year] = '';
        this.page[year] = 1;
        this.loadCompanies(year);
      });
    });
  }

  loadCompanies(year: any) {
    this.loading[year] = true;
    const from = ((this.page[year] || 1) - 1) * this.pageSize;
    const s = this.search[year] ? `&search=${encodeURIComponent(this.search[year])}` : '';
    const c = this.colorFilter[year] ? `&color=${this.colorFilter[year]}` : '';
    const filter = `?year=${year}&sector=${this.selectedSector[year] || 'Default'}&from=${from}&size=${this.pageSize}${s}${c}`;
    this.riskService.getCompanyScores(filter).subscribe((data: any) => {
      this.indicators[year] = data.indicators || [];
      this.records[year] = data.records || [];
      this.total[year] = data.total || 0;
      this.loading[year] = false;
    }, () => {
      this.loading[year] = false;
    });
  }

  getIndicatorScore(row: any, indicatorId: string): number {
    const entry = (row.indicatorScores || []).find((x: any) => x.id === indicatorId);
    return entry ? entry.score : 0;
  }

  setColorFilter(year: any, color: string) {
    this.colorFilter[year] = color;
    this.page[year] = 1;
    this.loadCompanies(year);
  }

  onSearchChange(year: any) {
    clearTimeout(this.searchTimers[year]);
    this.searchTimers[year] = setTimeout(() => {
      this.page[year] = 1;
      this.loadCompanies(year);
    }, 400);
  }

  onPageChange(year: any, page: number) {
    this.page[year] = page;
    this.loadCompanies(year);
  }

  goToBreakdown(row: any, year: any) {
    const ref = this.modalService.open(RiskBreakdownComponent, { size: 'lg', scrollable: true, centered: true, backdrop: 'static' });
    ref.componentInstance.match = row.match;
    ref.componentInstance.companyName = row.companyName;
    ref.componentInstance.year = year;
    ref.componentInstance.sector = this.selectedSector[year] || 'Default';
  }

  exportCompanyScores(year: any) {
    this.exporting[year] = true;
    const s = this.search[year] ? `&search=${encodeURIComponent(this.search[year])}` : '';
    const filter = `?year=${year}&sector=${this.selectedSector[year] || 'Default'}${s}`;
    this.riskService.exportCompanyScores$(filter).subscribe(blob => {
      saveAs(blob, `risk-${year}.xlsx`);
      this.exporting[year] = false;
    }, () => {
      this.exporting[year] = false;
    });
  }

  goToProfile(row: any) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/company/add-edit', row.payeeId])
    );
    window.open(url, '_blank');
  }
}
